import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  input,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { Params, RouterLink } from '@angular/router';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { FirstNameShowComponent } from '@features/first-name-show/first-name-show.component';

import { ChecklistToggleRow } from '@models/checklist-toggle/checklist-toggle.model';
import { ChecklistToggleStore } from '@models/checklist-toggle/checklist-toggle.store';

import { ButtonComponent } from '@shared/components/button/button.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import { listUsersQuery, PermissionType } from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { QuarterCloseNotesQuery } from '@shared/store/quarter-close-notes/quarter-close-notes.query';
import { QuarterCloseNotesStore } from '@shared/store/quarter-close-notes/quarter-close-notes.store';
import { QuarterCloseChecklistRowTitles } from '@shared/store/workflow/workflow-types';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { WorkflowService } from '@shared/store/workflow/workflow.service';
import { Workflow } from '@shared/store/workflow/workflow.store';
import { Utils } from '@shared/utils/utils';

import { QuarterCloseChecklistDirective } from '../../directives/quarter-close-checklist.directive';
import {
  ChecklistComponent,
  ChecklistComponentAddPoReportUploadFn,
  ChecklistComponentChangeLockFn,
  ChecklistComponentVendorEstimateUploadFn,
  QuarterCloseChecklistRowComponent,
  QuarterCloseChecklistVendorEstimateSummary,
} from '../../models/quarter-close-checklist.model';
import { ChecklistNotesComponent } from '../checklist-notes/checklist-notes.component';
import { ForecastCurvesRowTitleComponent } from './forecast-curves-row-title/forecast-curves-row-title.component';
import { GatherContractRowTitleComponent } from './gather-contract-row-title/gather-contract-row-title.component';
import { GatherInvoicesRowTitleComponent } from './gather-invoices-row-title.component';
import { MonthlyReviewNotesCounterComponent } from '../monthly-review-notes-counter/monthly-review-notes-counter.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    IconComponent,
    GatherContractRowTitleComponent,
    ForecastCurvesRowTitleComponent,
    GatherInvoicesRowTitleComponent,
    RouterLink,
    MonthlyReviewNotesCounterComponent,
    FirstNameShowComponent,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgOptionComponent,
    TooltipDirective,
    ButtonComponent,
    QuarterCloseChecklistDirective,
    ChecklistNotesComponent,
    AsyncPipe,
    DatePipe,
  ],
  selector: 'aux-checklist-row-info',
  styleUrls: ['../../quarter-close-checklist.component.scss'],
  templateUrl: './checklist-row-info.component.html',
})
export class ChecklistRowInfoComponent implements OnChanges {
  private authQuery = inject(AuthQuery);
  private cdr = inject(ChangeDetectorRef);
  private checklistToggleStore = inject(ChecklistToggleStore);
  private readonly destroyRef = inject(DestroyRef);
  private launchDarklyService = inject(LaunchDarklyService);
  private overlayService = inject(OverlayService);
  private quarterCloseNotesQuery = inject(QuarterCloseNotesQuery);
  private quarterCloseNotesStore = inject(QuarterCloseNotesStore);
  private workflowQuery = inject(WorkflowQuery);
  private workflowService = inject(WorkflowService);

  customRoute = input('');

  @Input() addPoReportUploadClick!: ChecklistComponentAddPoReportUploadFn;
  assignToFormControl = new UntypedFormControl(null);
  @Input() changeLockStatus!: ChecklistComponentChangeLockFn;
  @Input() checklistAdminPermission = false;
  @Input() checklistComponent!: ChecklistComponent<QuarterCloseChecklistRowComponent>;
  @ViewChild(QuarterCloseChecklistDirective)
  checklistComponentHost!: QuarterCloseChecklistDirective;
  @Input() detailRowContainerClassName = 'checklist-row-info-detail-container w-full mt-4 p-4';
  @Input() disabledLock = false;
  @Input() disabledRow = false;
  @Input() permissionType: null | PermissionType = null;
  usersList$ = new BehaviorSubject<listUsersQuery[]>([]);
  externalUserWithPermissionsList$ = this.usersList$.pipe(
    map((listUsers) =>
      listUsers.filter(
        (user) =>
          !user.email.includes('@auxili.us') &&
          user.permissions.some(
            (permission) =>
              permission.permission_type === this.permissionType &&
              permission.permission_roles === 'E'
          )
      )
    )
  );
  @Input() hasUserPermissions = false;
  isAdmin = false;
  isChecklistTimestampEnabled = this.launchDarklyService.$select(
    (flags) => flags.checklist_timestamp_last_update
  );
  @Input() isPastQuarterMonth = false;
  locked = false;
  lockedBy = '';
  lockedDate = '';
  @ViewChild(ChecklistNotesComponent) notes!: ChecklistNotesComponent;
  @Input() open = false;
  opened$ = new BehaviorSubject<boolean>(false);
  originalWorkflow?: Workflow;
  @Input() queryParams: Params = {};
  route = '';
  @Input() rowId!: ChecklistToggleRow;
  rowTitles = QuarterCloseChecklistRowTitles;
  @Input() selectedMonth = '';
  @Input() title = '';
  @Output() toggleOpened = new EventEmitter<boolean>();
  @Input() tooltip = '';
  user = '';
  @Input() vendorEstimateSummaries!: QuarterCloseChecklistVendorEstimateSummary[];
  @Input() vendorEstimateUploadClick!: ChecklistComponentVendorEstimateUploadFn;
  @Input() workflow!: Workflow;
  workflow$ = new BehaviorSubject<null | Workflow>(null);
  workflowSubscription!: Subscription;
  zeroHyphen = Utils.zeroHyphen;

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (simpleChanges.open) {
      this.opened$.next(this.open);
    }

    if (this.checklistComponent && this.open) {
      this.initializeChecklistComponent();
    }

    if (this.workflow) {
      this.initializeWorkflow();
      this.workflow$.next(this.workflow);
    } else if (!this.workflow) {
      this.resetWorkflow();
    }
  }

  async changeAssign(workflow: Workflow) {
    const selectedUser = this.usersList$
      .getValue()
      .find((user) => user.sub === this.assignToFormControl.value);
    const currentUserID = this.authQuery.getValue().sub;
    const updateLockedBy = this.usersList$.getValue().find((user) => user.sub === currentUserID);
    const currenDate = dayjs(new Date()).format('YYYY-MM-DD');

    await this.changeLockStatus(
      this.locked,
      {
        ...workflow,
        properties: {
          ...workflow.properties,
          assigned_by: workflow.updated_by,
          assigned_date: currenDate,
          assigned_to: selectedUser?.sub ? selectedUser?.sub : '',
        },
        updated_by: currentUserID ? currentUserID : workflow.updated_by,
        updatedAuthorFullName: updateLockedBy?.given_name + ' ' + updateLockedBy?.family_name,
      },
      true
    );
  }

  getLockBtnTooltip(): string {
    if (!this.hasUserPermissions) {
      return MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
    }

    return this.disabledLock ? this.tooltip : '';
  }

  getUnlockBtnTooltip(): string {
    return this.hasUserPermissions || this.isPastQuarterMonth
      ? ''
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION;
  }

  initializeChecklistComponent(): void {
    setTimeout(() => {
      if (!this.checklistComponent || !this.open || !this.workflow) {
        return;
      }

      const { viewContainerRef } = this.checklistComponentHost;

      viewContainerRef.clear();

      const componentRef = viewContainerRef.createComponent<QuarterCloseChecklistRowComponent>(
        this.checklistComponent.component
      );

      componentRef.instance.parent = this;

      if (this.checklistComponent.id === 'GatherEstimates') {
        componentRef.instance.data = {
          disabledLock: this.disabledLock,
          vendorEstimateSummaries: this.vendorEstimateSummaries,
          vendorEstimateUploadClick: this.vendorEstimateUploadClick,
        };
      }

      if (this.checklistComponent.id === 'GatherContracts') {
        componentRef.instance.data = {
          addPoReportUploadClick: this.addPoReportUploadClick,
          disabledLock: this.locked,
        };
      }

      this.checklistComponentHost.changeDetectorRef.detectChanges();
    }, 0);
  }

  initializeWorkflow() {
    const previousWorkflow = this.originalWorkflow;

    this.originalWorkflow = this.workflowQuery.getEntity(this.workflow.id);

    if (previousWorkflow?.id !== this.originalWorkflow?.id) {
      this.quarterCloseNotesStore.updateMonthlyReviewNotesCount(
        this.workflow.id,
        this.originalWorkflow?.monthly_review_note_count || 0
      );
    }

    this.isAdmin = this.authQuery.isAuxAdmin();
    this.lockedBy = this.workflow.updatedAuthorFullName;
    this.lockedDate = this.workflow.update_date;
    this.locked = this.workflow.properties.locked;
    this.route = this.workflow.properties.route ? `/${this.workflow.properties.route}` : '';
    this.user = '';

    if (this.workflowSubscription) {
      this.workflowSubscription.unsubscribe();
    }

    this.workflowSubscription = this.workflowService.listUsers$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => {
        this.usersList$.next([...users]);

        const selectedUser = users.find(
          (user) =>
            !user.email.includes('@auxili.us') &&
            user?.sub === this.workflow?.properties?.assigned_to &&
            user.permissions.some(
              (permission) =>
                permission.permission_type === this.permissionType &&
                permission.permission_roles === 'E'
            )
        );

        if (selectedUser) {
          this.user = `${selectedUser?.given_name} ${selectedUser?.family_name}`;
        }

        this.assignToFormControl.setValue(selectedUser ? selectedUser.sub : null);
        this.cdr.markForCheck();
      });
  }

  resetWorkflow() {
    this.lockedBy = '';
    this.lockedDate = '';
    this.locked = false;
    this.route = '';
  }

  async toggleRow(): Promise<void> {
    if (this.workflow) {
      if (this.open !== true) {
        this.toggleOpened.emit();
      }

      if (
        this.open === true &&
        this.quarterCloseNotesQuery.notes()[this.workflow.id]?.dirty?.someEditorDirty
      ) {
        const result = this.overlayService.openUnsavedChangesConfirmation();
        const event = await firstValueFrom(result.afterClosed$);

        if (event.data) {
          this.checklistToggleStore.toggleRow(this.rowId);
        }
      } else {
        this.checklistToggleStore.toggleRow(this.rowId);
      }
    }
  }
}
